import { Alert, Badge, Button, Group, Modal, Stack, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconClock, IconX } from "@tabler/icons-react";
import { useMemo, useState } from "react";

import { ZBoardTemplateStatus } from "@schemas/issueBoardSchema";
import { z } from "zod";
import { usePatchBoardsTemplateActionMutation } from "../../../../../openapi";
import { selectCurrentProjectId } from "../../../../global/globalSliceCommon";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { issueBoardRTK } from "../IssueBoardRTK";
import { selectHttpIssueBoard } from "../redux/selectors/issueRtkSelectors";

type TemplateStatusBannerProps = {
  isAdminOrCanApprove?: boolean;
};

type TemplateStatus = z.infer<typeof ZBoardTemplateStatus>;

const getStatusColor = (status: TemplateStatus) => {
  return status === "AKTIV" ? "green" : "gray";
};

const IssueBoardMcTemplateBanner: React.FC<TemplateStatusBannerProps> = ({ isAdminOrCanApprove = true }) => {
  const dispatch = useAppDispatch();
  const issueBoard = useAppSelector(selectHttpIssueBoard);
  const projectId = useAppSelector(selectCurrentProjectId);

  const [opened, setOpened] = useState(false);
  const [patchAction, { isLoading: isPatching }] = usePatchBoardsTemplateActionMutation();

  const currentStatus = issueBoard.template.status != null ? issueBoard.template.status : "IKKE AKTIV";
  const targetStatus = useMemo<TemplateStatus>(
    () => (currentStatus === "AKTIV" ? "IKKE AKTIV" : "AKTIV"),
    [currentStatus],
  );
  const approvedBy = issueBoard.template.approvedBy;
  const approvalDate = issueBoard.template.approvalDate
    ? new Date(issueBoard.template.approvalDate).toLocaleDateString("nb-NO")
    : null;
  const isActive = currentStatus === "AKTIV";

  const closeModal = () => {
    setOpened(false);
  };

  const handleSubmit = async () => {
    if (!projectId) {
      notifications.show({
        title: "Feil",
        message: "Mangler prosjekt for statusendring",
        color: "red",
        icon: <IconX size={16} />,
      });
      return;
    }

    try {
      const updatedTemplate = await patchAction({
        body: {
          projectId,
          boardId: issueBoard._id,
          action: targetStatus,
        },
      }).unwrap();

      dispatch(
        issueBoardRTK.util.updateQueryData(
          "getSingleIssueBoard",
          { projectId, issueBoardId: issueBoard._id },
          (draft) => {
            draft.template = updatedTemplate.template as any;
          },
        ),
      );

      dispatch(
        issueBoardRTK.util.updateQueryData("getIssueBoards", { projectId }, (draft) => {
          const board = draft.find((item) => item._id === issueBoard._id);
          if (board) {
            board.template = updatedTemplate.template as any;
          }
        }),
      );

      notifications.show({
        title: "Status oppdatert",
        message: `Malen er nå satt til ${targetStatus}`,
        color: "green",
        icon: <IconCheck size={16} />,
      });

      closeModal();
    } catch (err: any) {
      notifications.show({
        title: "Feil",
        message: err?.data?.message || err?.data?.error?.message || "Kunne ikke oppdatere status",
        color: "red",
        icon: <IconX size={16} />,
      });
    }
  };

  return (
    <>
      <Alert
        color={getStatusColor(currentStatus)}
        variant="light"
        bg={isActive ? "green.2" : "yellow.2"}
        radius={0}
        p="sm"
        m="xs"
      >
        <Group justify="space-between" wrap="wrap">
          <Group gap="sm">
            {isActive ? <IconCheck size={20} /> : <IconClock size={20} />}
            <Text fw={600} size="sm">
              Malstatus:
            </Text>
            <Badge size="sm" color={getStatusColor(currentStatus)} variant="filled">
              {currentStatus}
            </Badge>
            {isActive && approvedBy && (
              <Text size="sm" c="dimmed">
                Aktivert av <strong>{approvedBy.name}</strong> den {approvalDate}
              </Text>
            )}
            {!isActive && (
              <Text size="sm" c="dimmed">
                Denne malen er ikke aktiv enda. Kontakt mcapps@multiconsult.no for å få den aktivert.
              </Text>
            )}
          </Group>
          {isAdminOrCanApprove && (
            <Button
              variant="outline"
              size="sm"
              color={isActive ? "gray" : "blue"}
              onClick={() => setOpened(true)}
              disabled={isPatching}
            >
              Endre status
            </Button>
          )}
        </Group>
      </Alert>

      <Modal opened={opened} onClose={closeModal} title={<Title order={3}>Endre malstatus</Title>} centered size="md">
        <Stack>
          <Text size="sm">
            Malen er <strong>{currentStatus}</strong>. Bekreft for å sette malen til <strong>{targetStatus}</strong>.
          </Text>

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={closeModal}>
              Avbryt
            </Button>
            <Button loading={isPatching} color={targetStatus === "AKTIV" ? "green" : "blue"} onClick={handleSubmit}>
              Bekreft endring
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default IssueBoardMcTemplateBanner;
